/**
 * Document Templates — Word (.docx) and PDF generators for resumes and cover letters.
 * Uses jsPDF for PDF and docx package for Word.
 * All generation happens client-side (no backend needed).
 */

import jsPDF from 'jspdf';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  BorderStyle,
  TableRow,
  TableCell,
  Table,
  WidthType,
  TabStopType,
} from 'docx';

// ── Type Definitions ────────────────────────────────────────────────────────

export interface ResumeData {
  contact: { name: string; email: string; phone: string; location: string };
  headline: string;
  professional_summary: string;
  core_competencies: string[];
  experience: { role: string; company: string; dates: string; bullets: string[] }[];
  education: { degree: string; school: string; year: string }[];
  certifications?: string[];
  technical_skills?: Record<string, string[]>;
}

export interface CoverLetterData {
  date: string;
  recipient: string;
  company: string;
  subject: string;
  opening: string;
  value_prop: string;
  closing: string;
  sign_off: string;
}

export interface CvData {
  contact: { name: string; email: string; phone: string; location: string };
  profile_summary: string;
  experience: { role: string; company: string; dates: string; description: string; bullets: string[] }[];
  earlier_experience?: string[];
  education: { degree: string; field: string; school: string; year: string; honors?: string }[];
  certifications: string[];
  skills: Record<string, string[]>;
  key_achievements: string[];
}

// ── Text sanitization (defence-in-depth; server QA gate also cleans) ─────────
// Strips markdown/ampersand artifacts and em/en dashes that must never reach the
// final Word/PDF (e.g. `**HEADING**`, `****&****`, U+2014). Preserves "C#"/"C++"
// (only strips `#` at line start as a markdown heading, never mid-token).

function sanitizeText(s: string): string {
  if (!s) return '';
  return s
    .replace(/[—–]/g, '-')          // em/en dash → hyphen
    .replace(/\*+\s*&\s*\*+/g, ' & ')         // ****&**** → &
    .replace(/\*\*([\s\S]*?)\*\*/g, '$1')     // **bold** → text
    .replace(/__([\s\S]*?)__/g, '$1')         // __bold__ → text
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')       // markdown headings
    .replace(/^\s{0,3}[*+]\s+/gm, '')         // markdown bullets at line start
    .replace(/\*{2,}/g, '')                   // stray ** runs
    .replace(/&amp;/gi, '&')                  // decode ampersand entity
    .replace(/&nbsp;/gi, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/** Deep-clone an object, sanitizing every string leaf. Call once at each entry. */
function sanitizeData<T>(obj: T): T {
  if (typeof obj === 'string') return sanitizeText(obj) as unknown as T;
  if (Array.isArray(obj)) return obj.map((v) => sanitizeData(v)) as unknown as T;
  if (obj && typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) out[k] = sanitizeData(v);
    return out as unknown as T;
  }
  return obj;
}

// ── Download Helper ─────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Build a safe download filename like CV_Benedict_Nwankwo_Acme_CIO.pdf */
function docFileName(prefix: string, name: string, company?: string, role?: string): string {
  const s = (v?: string) => (v || '').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
  return [prefix, s(name), s(company), s(role)].filter(Boolean).join('_') + '.pdf';
}

// ── Colors ──────────────────────────────────────────────────────────────────

const DARK_BLUE = '#1F3864';   // navy (brief token)
const MED_BLUE = '#2E5496';    // rule/accent (brief token)
const BODY_GRAY = '#555555';   // body text (brief token)
const LIGHT_GRAY = '#9ca3af';
const RULE_COLOR = '#e5e7eb';

// ── RESUME PDF ──────────────────────────────────────────────────────────────

export function downloadResumePdf(resume: ResumeData, jobTitle: string, company: string) {
  resume = sanitizeData(resume);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const W = 215.9;
  const MARGIN = 16;
  const CONTENT_W = W - MARGIN * 2;
  let y = 18;

  // Helper to add text and return new y
  const addText = (
    text: string,
    x: number,
    size: number,
    color: string,
    opts: { bold?: boolean; align?: 'left' | 'right' | 'center'; maxWidth?: number } = {},
  ) => {
    doc.setFontSize(size);
    doc.setTextColor(color);
    if (opts.bold) doc.setFont('helvetica', 'bold');
    else doc.setFont('helvetica', 'normal');
    if (opts.align === 'right') {
      doc.text(text, W - MARGIN, y, { align: 'right' });
    } else if (opts.align === 'center') {
      doc.text(text, W / 2, y, { align: 'center' });
    } else {
      doc.text(text, x, y, opts.maxWidth ? { maxWidth: opts.maxWidth } : undefined);
    }
  };

  const rule = (thickness = 0.3, color = RULE_COLOR) => {
    doc.setDrawColor(color);
    doc.setLineWidth(thickness);
    doc.line(MARGIN, y, W - MARGIN, y);
    y += 3;
  };

  const sectionHeader = (title: string) => {
    y += 4;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(DARK_BLUE);
    doc.text(title.toUpperCase(), MARGIN, y);
    y += 1.5;
    doc.setDrawColor(MED_BLUE);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, y, W - MARGIN, y);
    y += 4;
  };

  const checkPageBreak = (needed = 10) => {
    if (y > 267 - needed) {
      doc.addPage();
      y = 18;
    }
  };

  // ── Header ──
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(DARK_BLUE);
  doc.text(resume.contact.name.toUpperCase(), MARGIN, y);

  // Contact info on right
  const contactParts = [resume.contact.email, resume.contact.phone, resume.contact.location].filter(Boolean);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(LIGHT_GRAY);
  doc.text(contactParts.join('  |  '), W - MARGIN, y, { align: 'right' });
  y += 5;

  // Headline
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(MED_BLUE);
  doc.text(resume.headline, MARGIN, y);
  y += 2;

  rule(0.6, MED_BLUE);
  y += 2;

  // ── Professional Summary ──
  sectionHeader('Professional Summary');
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(BODY_GRAY);
  const summaryLines = doc.splitTextToSize(resume.professional_summary, CONTENT_W);
  doc.text(summaryLines, MARGIN, y);
  y += summaryLines.length * 4.5 + 2;

  // ── Core Competencies ──
  // Two columns with per-cell wrapping. Executive competency phrases are long
  // ("Program Management - AI/ML and Technical Initiatives"); a 3-col grid can't
  // fit them and jsPDF does not auto-wrap fixed-position text, so it would clip.
  if (resume.core_competencies?.length > 0) {
    sectionHeader('Core Competencies');
    const cols = 2;
    const colW = CONTENT_W / cols;
    const cellTextW = colW - 8; // leave room for the "•  " marker + inter-column gutter
    const skills = resume.core_competencies.slice(0, 12);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(BODY_GRAY);
    for (let i = 0; i < skills.length; i += cols) {
      checkPageBreak(10);
      let rowLines = 1;
      for (let j = 0; j < cols; j++) {
        const skill = skills[i + j];
        if (!skill) continue;
        const lines = doc.splitTextToSize(`•  ${skill}`, cellTextW);
        doc.text(lines, MARGIN + j * colW, y);
        rowLines = Math.max(rowLines, lines.length);
      }
      y += rowLines * 4.5 + 1.5;
    }
  }

  // ── Experience ──
  if (resume.experience?.length > 0) {
    sectionHeader('Professional Experience');
    for (const exp of resume.experience) {
      checkPageBreak(15);
      // Role + dates on same line
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(DARK_BLUE);
      doc.text(exp.role.toUpperCase(), MARGIN, y);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(LIGHT_GRAY);
      doc.text(exp.dates, W - MARGIN, y, { align: 'right' });
      y += 4.5;
      // Company
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(MED_BLUE);
      doc.text(exp.company, MARGIN, y);
      y += 4.5;
      // Bullets
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(BODY_GRAY);
      for (const bullet of exp.bullets ?? []) {
        checkPageBreak(6);
        const lines = doc.splitTextToSize(`•  ${bullet}`, CONTENT_W - 4);
        doc.text(lines, MARGIN + 3, y);
        y += lines.length * 4.5;
      }
      y += 3;
    }
  }

  // ── Education ──
  if (resume.education?.length > 0) {
    sectionHeader('Education');
    for (const edu of resume.education) {
      checkPageBreak(8);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(DARK_BLUE);
      doc.text(edu.degree, MARGIN, y);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(LIGHT_GRAY);
      doc.text(edu.year, W - MARGIN, y, { align: 'right' });
      y += 4.5;
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(BODY_GRAY);
      doc.text(edu.school, MARGIN, y);
      y += 6;
    }
  }

  // ── Certifications ──
  if ((resume.certifications?.length ?? 0) > 0) {
    sectionHeader('Certifications');
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(BODY_GRAY);
    for (const cert of resume.certifications ?? []) {
      checkPageBreak(5);
      const lines = doc.splitTextToSize(`•  ${cert}`, CONTENT_W);
      doc.text(lines, MARGIN, y);
      y += lines.length * 4.5 + 0.5;
    }
  }

  // ── Technical Skills ──
  const techSkills = resume.technical_skills;
  if (techSkills && Object.keys(techSkills).length > 0) {
    sectionHeader('Technical Skills');
    for (const [cat, skills] of Object.entries(techSkills)) {
      // Category label on its own line — same fixed-gutter overlap risk as the CV
      // skills section when a category name is long.
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(BODY_GRAY);
      const skillLines = doc.splitTextToSize(skills.join(', '), CONTENT_W - 4);
      checkPageBreak(4.5 + skillLines.length * 4.5 + 3);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(DARK_BLUE);
      doc.text(`${cat}`, MARGIN, y);
      y += 4.5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(BODY_GRAY);
      doc.text(skillLines, MARGIN + 3, y);
      y += skillLines.length * 4.5 + 3;
    }
  }

  const safeName = resume.contact.name.replace(/[^a-z0-9]/gi, '_');
  doc.save(`Resume_${safeName}_${(jobTitle || 'Application').replace(/[^a-z0-9]/gi, '_')}.pdf`);
}

// ── COVER LETTER PDF ─────────────────────────────────────────────────────────

function renderCoverLetterInto(doc: jsPDF, cl: CoverLetterData, contactName: string, contactEmail: string, contactPhone: string): void {
  cl = sanitizeData(cl);
  contactName = sanitizeText(contactName);
  const W = 215.9;
  const MARGIN = 20;
  const CONTENT_W = W - MARGIN * 2;
  let y = 20;

  // ── Letterhead ──
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(DARK_BLUE);
  doc.text(contactName.toUpperCase(), MARGIN, y);
  y += 6;

  const contactLine = [contactEmail, contactPhone].filter(Boolean).join('   |   ');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(LIGHT_GRAY);
  doc.text(contactLine, MARGIN, y);
  y += 2;

  doc.setDrawColor(MED_BLUE);
  doc.setLineWidth(0.7);
  doc.line(MARGIN, y, W - MARGIN, y);
  y += 12;

  // Date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(BODY_GRAY);
  doc.text(cl.date, MARGIN, y);
  y += 10;

  // Recipient
  doc.setFontSize(10);
  doc.text(cl.recipient, MARGIN, y);
  y += 5;
  doc.text(cl.company, MARGIN, y);
  y += 12;

  // Subject
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(DARK_BLUE);
  doc.text(`RE: ${cl.subject}`, MARGIN, y);
  y += 10;

  // Salutation
  doc.setFontSize(10.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(BODY_GRAY);
  doc.text(`Dear ${cl.recipient},`, MARGIN, y);
  y += 8;

  // Paragraphs
  const paragraphs = [cl.opening, cl.value_prop, cl.closing];
  for (const para of paragraphs) {
    const lines = doc.splitTextToSize(para, CONTENT_W);
    if (y + lines.length * 5.5 > 258) {
      doc.addPage();
      y = 20;
    }
    doc.text(lines, MARGIN, y);
    y += lines.length * 5.5 + 6;
  }

  // Sign-off
  y += 4;
  doc.text(cl.sign_off + ',', MARGIN, y);
  y += 12;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(DARK_BLUE);
  doc.text(contactName, MARGIN, y);

}

export function downloadCoverLetterPdf(cl: CoverLetterData, contactName: string, contactEmail: string, contactPhone: string): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  renderCoverLetterInto(doc, cl, contactName, contactEmail, contactPhone);
  doc.save(docFileName('CoverLetter', contactName, cl.company));
}

// ── RESUME WORD (.docx) ──────────────────────────────────────────────────────

export async function downloadResumeDocx(resume: ResumeData, jobTitle: string) {
  resume = sanitizeData(resume);
  const children: (Paragraph | Table)[] = [];

  // Name
  children.push(
    new Paragraph({
      children: [new TextRun({ text: resume.contact.name.toUpperCase(), bold: true, size: 44, color: '1e3a5f' })],
      spacing: { after: 80 },
    }),
  );

  // Contact line
  const contactParts = [resume.contact.email, resume.contact.phone, resume.contact.location].filter(Boolean);
  children.push(
    new Paragraph({
      children: [new TextRun({ text: contactParts.join('   |   '), size: 18, color: '9ca3af' })],
      spacing: { after: 40 },
    }),
  );

  // Headline
  children.push(
    new Paragraph({
      children: [new TextRun({ text: resume.headline, bold: true, size: 22, color: '2563eb' })],
      spacing: { after: 80 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: '2563eb', space: 4 } },
    }),
  );

  const sectionTitle = (text: string): Paragraph =>
    new Paragraph({
      children: [new TextRun({ text: text.toUpperCase(), bold: true, size: 19, color: '1e3a5f' })],
      spacing: { before: 200, after: 60 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: '2563eb', space: 3 } },
    });

  // Professional Summary
  children.push(sectionTitle('Professional Summary'));
  children.push(
    new Paragraph({
      children: [new TextRun({ text: resume.professional_summary, size: 20, color: '374151' })],
      spacing: { after: 80 },
    }),
  );

  // Core Competencies — 3 column table
  if (resume.core_competencies?.length > 0) {
    children.push(sectionTitle('Core Competencies'));
    const skills = resume.core_competencies.slice(0, 12);
    const rows: TableRow[] = [];
    for (let i = 0; i < skills.length; i += 3) {
      rows.push(
        new TableRow({
          children: [0, 1, 2].map((j) =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: `•  ${skills[i + j] ?? ''}`, size: 19, color: '374151' })],
                }),
              ],
              borders: {
                top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
                bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
                left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
                right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
              },
              width: { size: 33, type: WidthType.PERCENTAGE },
            }),
          ),
        }),
      );
    }
    if (rows.length > 0) {
      children.push(
        new Table({
          rows,
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
            bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
            left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
            right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
          },
        }),
      );
    }
  }

  // Experience
  if (resume.experience?.length > 0) {
    children.push(sectionTitle('Professional Experience'));
    resume.experience.forEach((exp, idx) => {
      if (idx > 0) children.push(new Paragraph({ children: [], spacing: { after: 0 } })); // one line space between roles
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: exp.role.toUpperCase(), bold: true, size: 21, color: DARK_BLUE.replace('#', '') }),
            new TextRun({ text: `\t${exp.dates}`, size: 19, color: '9ca3af' }),
          ],
          tabStops: [{ type: TabStopType.RIGHT, position: 9360 }],
          spacing: { before: 140, after: 30 },
        }),
      );
      children.push(
        new Paragraph({
          children: [new TextRun({ text: exp.company, bold: true, size: 20, color: MED_BLUE.replace('#', '') })],
          spacing: { after: 60 },
        }),
      );
      for (const bullet of exp.bullets ?? []) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: bullet, size: 19, color: '374151' })],
            bullet: { level: 0 },
            spacing: { after: 40 },
          }),
        );
      }
    });
  }

  // Education
  if (resume.education?.length > 0) {
    children.push(sectionTitle('Education'));
    for (const edu of resume.education) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: edu.degree, bold: true, size: 21, color: '1e3a5f' }),
            new TextRun({ text: `  |  ${edu.year}`, size: 19, color: '9ca3af' }),
          ],
          spacing: { before: 60, after: 30 },
        }),
      );
      children.push(
        new Paragraph({
          children: [new TextRun({ text: edu.school, size: 20, color: '374151' })],
          spacing: { after: 60 },
        }),
      );
    }
  }

  // Certifications
  if ((resume.certifications?.length ?? 0) > 0) {
    children.push(sectionTitle('Certifications'));
    for (const cert of resume.certifications ?? []) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: cert, size: 19, color: '374151' })],
          bullet: { level: 0 },
          spacing: { after: 40 },
        }),
      );
    }
  }

  // Technical Skills
  const techSkills = resume.technical_skills;
  if (techSkills && Object.keys(techSkills).length > 0) {
    children.push(sectionTitle('Technical Skills'));
    for (const [cat, skills] of Object.entries(techSkills)) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${cat}: `, bold: true, size: 19, color: '1e3a5f' }),
            new TextRun({ text: skills.join(', '), size: 19, color: '374151' }),
          ],
          spacing: { after: 40 },
        }),
      );
    }
  }

  const doc = new Document({
    styles: { default: { document: { run: { font: 'Arial' } } } },
    sections: [
      {
        properties: {
          page: { margin: { top: 720, right: 900, bottom: 720, left: 900 } },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const safeName = resume.contact.name.replace(/[^a-z0-9]/gi, '_');
  downloadBlob(blob, `Resume_${safeName}_${(jobTitle || 'Application').replace(/[^a-z0-9]/gi, '_')}.docx`);
}

// ── COVER LETTER WORD (.docx) ───────────────────────────────────────────────

export async function downloadCoverLetterDocx(cl: CoverLetterData, contactName: string, contactEmail: string, contactPhone: string) {
  cl = sanitizeData(cl);
  contactName = sanitizeText(contactName);
  const contactLine = [contactEmail, contactPhone].filter(Boolean).join('   |   ');

  const doc = new Document({
    styles: { default: { document: { run: { font: 'Arial' } } } },
    sections: [
      {
        properties: {
          page: { margin: { top: 900, right: 1080, bottom: 900, left: 1080 } },
        },
        children: [
          // Name
          new Paragraph({
            children: [new TextRun({ text: contactName.toUpperCase(), bold: true, size: 40, color: '1e3a5f' })],
            spacing: { after: 60 },
          }),
          // Contact
          new Paragraph({
            children: [new TextRun({ text: contactLine, size: 18, color: '9ca3af' })],
            spacing: { after: 40 },
            border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: '2563eb', space: 6 } },
          }),
          // Spacer
          new Paragraph({ children: [new TextRun({ text: '' })], spacing: { after: 240 } }),
          // Date
          new Paragraph({
            children: [new TextRun({ text: cl.date, size: 21, color: '374151' })],
            spacing: { after: 200 },
          }),
          // Recipient
          new Paragraph({
            children: [new TextRun({ text: cl.recipient, size: 21, color: '374151' })],
            spacing: { after: 60 },
          }),
          new Paragraph({
            children: [new TextRun({ text: cl.company, size: 21, color: '374151' })],
            spacing: { after: 200 },
          }),
          // Subject
          new Paragraph({
            children: [new TextRun({ text: `RE: ${cl.subject}`, bold: true, size: 21, color: '1e3a5f' })],
            spacing: { after: 200 },
          }),
          // Salutation
          new Paragraph({
            children: [new TextRun({ text: `Dear ${cl.recipient},`, size: 22, color: '374151' })],
            spacing: { after: 180 },
          }),
          // Opening
          new Paragraph({
            children: [new TextRun({ text: cl.opening, size: 22, color: '374151' })],
            spacing: { after: 180 },
            alignment: AlignmentType.JUSTIFIED,
          }),
          // Value prop
          new Paragraph({
            children: [new TextRun({ text: cl.value_prop, size: 22, color: '374151' })],
            spacing: { after: 180 },
            alignment: AlignmentType.JUSTIFIED,
          }),
          // Closing
          new Paragraph({
            children: [new TextRun({ text: cl.closing, size: 22, color: '374151' })],
            spacing: { after: 280 },
            alignment: AlignmentType.JUSTIFIED,
          }),
          // Sign-off
          new Paragraph({
            children: [new TextRun({ text: `${cl.sign_off},`, size: 22, color: '374151' })],
            spacing: { after: 400 },
          }),
          // Name
          new Paragraph({
            children: [new TextRun({ text: contactName, bold: true, size: 22, color: '1e3a5f' })],
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const safeName = contactName.replace(/[^a-z0-9]/gi, '_');
  const safeCompany = cl.company.replace(/[^a-z0-9]/gi, '_');
  downloadBlob(blob, `CoverLetter_${safeName}_${safeCompany}.docx`);
}

// ── CV PDF (Comprehensive Career Record) ─────────────────────────────────────

function buildCvDoc(cv: CvData, candidateName: string): jsPDF {
  cv = sanitizeData(cv);
  candidateName = sanitizeText(candidateName);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const W = 215.9;
  const M = 16;
  const CW = W - M * 2;
  let y = 0;

  const checkPage = (needed = 12) => {
    if (y > 267 - needed) { doc.addPage(); y = 18; }
  };

  const cvSection = (title: string) => {
    checkPage(16);
    y += 3;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#0f2137');
    doc.text(title.toUpperCase(), M, y);
    y += 1.5;
    doc.setDrawColor('#3b82f6');
    doc.setLineWidth(0.6);
    doc.line(M, y, W - M, y);
    y += 5;
  };

  // ── Header band ──
  doc.setFillColor('#0f2137');
  doc.rect(0, 0, W, 30, 'F');
  doc.setFillColor('#3b82f6');
  doc.rect(0, 0, 4, 30, 'F');
  doc.setFontSize(21);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor('#ffffff');
  doc.text((cv.contact.name || candidateName).toUpperCase(), M, 13);
  const cvContactStr = [cv.contact.email, cv.contact.phone, cv.contact.location].filter(Boolean).join('   ·   ');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor('#94a3b8');
  doc.text(cvContactStr, M, 22);
  y = 38;

  // ── Profile Summary ──
  if (cv.profile_summary?.trim()) {
    // Measure at the SAME size the body renders (9pt). splitTextToSize uses the
    // current font size, so wrapping at the leftover header size (7.5) produced
    // lines ~20% too wide that overran the page and clipped. CW-14 keeps ~7mm
    // padding on each side to match the M+7 text inset inside the box.
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const sumLines = doc.splitTextToSize(cv.profile_summary, CW - 14);
    const boxH = sumLines.length * 4.5 + 14;
    doc.setFillColor('#eff6ff');
    doc.rect(M, y - 2, CW, boxH, 'F');
    doc.setFillColor('#3b82f6');
    doc.rect(M, y - 2, 2.5, boxH, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#1e40af');
    doc.text('PROFESSIONAL PROFILE', M + 7, y + 4);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#374151');
    doc.text(sumLines, M + 7, y + 9);
    y += boxH + 8;
  }

  // ── Key Achievements ──
  if (cv.key_achievements?.length > 0) {
    cvSection('Key Achievements');
    for (const ach of cv.key_achievements.slice(0, 8)) {
      checkPage(8);
      doc.setFillColor('#3b82f6');
      doc.rect(M, y - 3.5, 1.5, 5, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor('#374151');
      const achLines = doc.splitTextToSize(ach, CW - 8);
      doc.text(achLines, M + 5, y);
      y += achLines.length * 4.5 + 2;
    }
    y += 2;
  }

  // ── Career History ──
  if (cv.experience?.length > 0) {
    cvSection('Career History');
    for (const exp of cv.experience) {
      checkPage(22);
      doc.setFillColor('#f8fafc');
      doc.rect(M, y - 4, CW, 7, 'F');
      doc.setFontSize(10.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor('#0f2137');
      doc.text(exp.role, M + 2, y);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor('#9ca3af');
      doc.text(exp.dates, W - M - 2, y, { align: 'right' });
      y += 5;
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor('#3b82f6');
      doc.text(exp.company, M + 2, y);
      y += 5;
      if (exp.description?.trim()) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor('#6b7280');
        const dLines = doc.splitTextToSize(exp.description, CW - 4);
        doc.text(dLines, M + 2, y);
        y += dLines.length * 4.5 + 2;
      }
      doc.setFont('helvetica', 'normal');
      doc.setTextColor('#374151');
      for (const bullet of exp.bullets ?? []) {
        checkPage(6);
        const bLines = doc.splitTextToSize(`•  ${bullet}`, CW - 8);
        doc.text(bLines, M + 5, y);
        y += bLines.length * 4.5;
      }
      y += 7; // one line space between roles
    }
    // Earlier Experiences — compact bullet list of older roles.
    if (cv.earlier_experience && cv.earlier_experience.length > 0) {
      checkPage(12);
      doc.setFontSize(9.5); doc.setFont('helvetica', 'bold'); doc.setTextColor('#0f2137');
      doc.text('Earlier Experiences', M, y); y += 5;
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor('#374151');
      for (const e of cv.earlier_experience) {
        checkPage(6);
        const lines = doc.splitTextToSize(`•  ${e}`, CW - 8);
        doc.text(lines, M + 5, y);
        y += lines.length * 4.5 + 1;
      }
      y += 3;
    }
  }

  // ── Skills ──
  if (cv.skills && Object.keys(cv.skills).length > 0) {
    cvSection('Skills & Expertise');
    for (const [cat, skills] of Object.entries(cv.skills)) {
      // Category label on its own line. Grouped labels ("Program & Project
      // Management") are far wider than any fixed inline gutter, so drawing the
      // skills at a fixed x overlapped the label. Measure at the render size.
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor('#374151');
      const skillLines = doc.splitTextToSize((skills as string[]).join(', '), CW - 4);
      checkPage(4.5 + skillLines.length * 4.5 + 3);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor('#0f2137');
      doc.text(`${cat}`, M, y);
      y += 4.5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor('#374151');
      doc.text(skillLines, M + 3, y);
      y += skillLines.length * 4.5 + 3;
    }
    y += 2;
  }

  // ── Education ──
  if (cv.education?.length > 0) {
    cvSection('Education');
    for (const edu of cv.education) {
      checkPage(12);
      const degreeLabel = [edu.degree, edu.field].filter(Boolean).join(', ');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor('#0f2137');
      doc.text(degreeLabel, M, y);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor('#9ca3af');
      doc.text(edu.year, W - M, y, { align: 'right' });
      y += 5;
      doc.setFontSize(9.5);
      doc.setTextColor('#374151');
      doc.text(edu.school, M, y);
      y += 5;
      if (edu.honors) {
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor('#3b82f6');
        doc.text(`  ${edu.honors}`, M + 3, y);
        y += 5;
      }
      y += 2;
    }
  }

  // ── Certifications ──
  if (cv.certifications?.length > 0) {
    cvSection('Certifications');
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#374151');
    for (const cert of cv.certifications) {
      checkPage(6);
      const lines = doc.splitTextToSize(`•  ${cert}`, CW);
      doc.text(lines, M, y);
      y += lines.length * 4.5 + 1;
    }
  }

  return doc;
}

export function downloadCvPdf(cv: CvData, candidateName: string, company = '', role = ''): void {
  const doc = buildCvDoc(cv, candidateName);
  doc.save(docFileName('CV', cv.contact.name || candidateName, company, role));
}

/** Consolidated application PDF: CV pages first, then the cover letter on a new page. */
export function downloadConsolidatedPdf(
  cv: CvData, cl: CoverLetterData, candidateName: string, email: string, phone: string, company = '', role = '',
): void {
  const doc = buildCvDoc(cv, candidateName);
  doc.addPage();
  renderCoverLetterInto(doc, cl, cv.contact.name || candidateName, email, phone);
  doc.save(docFileName('Application', cv.contact.name || candidateName, company, role));
}

// ── CV WORD (.docx) ──────────────────────────────────────────────────────────

export async function downloadCvDocx(cv: CvData): Promise<void> {
  cv = sanitizeData(cv);
  const children: (Paragraph | Table)[] = [];

  const cvTitle = (text: string): Paragraph =>
    new Paragraph({
      children: [new TextRun({ text: text.toUpperCase(), bold: true, size: 19, color: '0f2137' })],
      spacing: { before: 220, after: 60 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 5, color: '3b82f6', space: 3 } },
    });

  // Name
  children.push(
    new Paragraph({
      children: [new TextRun({ text: (cv.contact.name || '').toUpperCase(), bold: true, size: 46, color: '0f2137' })],
      spacing: { after: 60 },
    }),
  );

  // Contact line
  const cvContactLine = [cv.contact.email, cv.contact.phone, cv.contact.location].filter(Boolean).join('   |   ');
  children.push(
    new Paragraph({
      children: [new TextRun({ text: cvContactLine, size: 18, color: '9ca3af' })],
      spacing: { after: 40 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: '0f2137', space: 5 } },
    }),
  );

  // Profile Summary
  if (cv.profile_summary?.trim()) {
    children.push(cvTitle('Professional Profile'));
    children.push(
      new Paragraph({
        children: [new TextRun({ text: cv.profile_summary, size: 20, color: '374151' })],
        spacing: { after: 80 },
        indent: { left: 200 },
        border: { left: { style: BorderStyle.THICK, size: 12, color: '3b82f6', space: 8 } },
      }),
    );
  }

  // Key Achievements
  if (cv.key_achievements?.length > 0) {
    children.push(cvTitle('Key Achievements'));
    for (const ach of cv.key_achievements.slice(0, 8)) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: ach, size: 19, color: '374151' })],
          bullet: { level: 0 },
          spacing: { after: 40 },
        }),
      );
    }
  }

  // Career History
  if (cv.experience?.length > 0) {
    children.push(cvTitle('Career History'));
    cv.experience.forEach((exp, idx) => {
      if (idx > 0) children.push(new Paragraph({ children: [], spacing: { after: 0 } })); // one line space between roles
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: exp.role, bold: true, size: 22, color: DARK_BLUE.replace('#', '') }),
            new TextRun({ text: `\t${exp.dates}`, size: 18, color: '9ca3af' }),
          ],
          tabStops: [{ type: TabStopType.RIGHT, position: 9360 }],
          spacing: { before: 140, after: 30 },
        }),
      );
      children.push(
        new Paragraph({
          children: [new TextRun({ text: exp.company, bold: true, size: 20, color: MED_BLUE.replace('#', '') })],
          spacing: { after: 40 },
        }),
      );
      if (exp.description?.trim()) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: exp.description, size: 19, italics: true, color: '6b7280' })],
            spacing: { after: 60 },
          }),
        );
      }
      for (const bullet of exp.bullets ?? []) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: bullet, size: 19, color: '374151' })],
            bullet: { level: 0 },
            spacing: { after: 40 },
          }),
        );
      }
    });
    // Earlier Experiences — compact bullet list of older roles.
    if (cv.earlier_experience && cv.earlier_experience.length > 0) {
      children.push(new Paragraph({ children: [], spacing: { after: 0 } }));
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'Earlier Experiences', bold: true, size: 20, color: DARK_BLUE.replace('#', '') })],
          spacing: { before: 120, after: 40 },
        }),
      );
      for (const e of cv.earlier_experience) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: e, size: 19, color: '374151' })],
            bullet: { level: 0 },
            spacing: { after: 30 },
          }),
        );
      }
    }
  }

  // Skills
  if (cv.skills && Object.keys(cv.skills).length > 0) {
    children.push(cvTitle('Skills & Expertise'));
    for (const [cat, skills] of Object.entries(cv.skills)) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${cat}: `, bold: true, size: 19, color: '0f2137' }),
            new TextRun({ text: (skills as string[]).join(', '), size: 19, color: '374151' }),
          ],
          spacing: { after: 40 },
        }),
      );
    }
  }

  // Education
  if (cv.education?.length > 0) {
    children.push(cvTitle('Education'));
    for (const edu of cv.education) {
      const degreeLabel = [edu.degree, edu.field].filter(Boolean).join(', ');
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: degreeLabel, bold: true, size: 21, color: '0f2137' }),
            new TextRun({ text: `   ${edu.year}`, size: 18, color: '9ca3af' }),
          ],
          spacing: { before: 80, after: 30 },
        }),
      );
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: edu.school, size: 20, color: '374151' }),
            ...(edu.honors ? [new TextRun({ text: `   ${edu.honors}`, size: 18, italics: true, color: '3b82f6' })] : []),
          ],
          spacing: { after: 60 },
        }),
      );
    }
  }

  // Certifications
  if (cv.certifications?.length > 0) {
    children.push(cvTitle('Certifications'));
    for (const cert of cv.certifications) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: cert, size: 19, color: '374151' })],
          bullet: { level: 0 },
          spacing: { after: 40 },
        }),
      );
    }
  }

  const wordDoc = new Document({
    styles: { default: { document: { run: { font: 'Arial' } } } },
    sections: [{
      properties: { page: { margin: { top: 720, right: 900, bottom: 720, left: 900 } } },
      children,
    }],
  });

  const cvBlob = await Packer.toBlob(wordDoc);
  const cvSafeName = cv.contact.name.replace(/[^a-z0-9]/gi, '_');
  downloadBlob(cvBlob, `CV_${cvSafeName}.docx`);
}
